import type { consoleWarn } from "common/utilities/browser/consoleLog/consoleLog";
import { logError, type LogTarget } from "core/api/api";
import type { ResponseTypes } from "core/api/api.typesGuards";
import { isGet, isJsonResponse, isResponseWithMessages } from "core/api/apiHelpers";
import type { ErrorContext, ResponseContext } from "core/api/coreApiClient";
import type * as I from "core/api/coreApiClient";
import { enqueueErrorNotification } from "core/redux/actions/notification/notificationActions";
import { getStore } from "core/redux/store/configureStore";
import type { ErrorInfo } from "react";

class ExceptionLogger {
  static dispatchNotification(message: string) {
    getStore().dispatch(enqueueErrorNotification(message));
  }

  /**
   *
   * @param error Error object or a string message
   * @param params React component stack error object
   * @param details complementary information
   * @returns
   */
  static async log(error: Error | string, errorInfo: ErrorInfo | undefined, details: string[]) {
    return this.logToBackend(error, details);
  }

  /**
   * Log only responses that are not handled, see loggerMiddleware for details.
   * Create a log entry in the backend.
   * Also dispatch a notification.
   */
  static async logRequestError(
    error: Error | string,
    request: ResponseContext | ErrorContext,
    response: Response | undefined,
    details?: string[]
  ) {
    let data;

    const logTarget: LogTarget = request.url.match("retryOnFailure=true") ? "DEBUG" : "ERROR";

    try {
      if (response) {
        if (isJsonResponse(response)) {
          data = await response.clone().json();
          const genericResponse = data as ResponseTypes;

          if (isResponseWithMessages(genericResponse)) {
            return; // Already logged by the backend
          }
        } else {
          // Could also be some generic tomcat response (non-JSON)
          const text = await response.clone().text();
          data = `Response: ${text}`;
        }
      } else {
        // No response at all: usually a network issue
        data = "No response data.";
      }
    } catch (e) {
      data = "Error parsing the response data.";
    }

    try {
      consoleWarn([
        "LOGGING REQUEST ERROR:",
        `URL: ${JSON.stringify(request.url)}`,
        `Error: ${JSON.stringify(error)}`,
        `Data: ${JSON.stringify(data)}`,
      ]);

      const payload = await this.logRequestErrorToBackend(
        error,
        request,
        response,
        JSON.stringify(data),
        details,
        logTarget
      );

      if (payload && logTarget === "ERROR" && request.url.match("client-build-id") === null) {
        let message: string | undefined = payload.incidentId;

        if (request.url.match("/paging")) {
          message = `Table pagination couldn't be loaded: ${message}`;
        } else if (request.url.match("/fetchCommand")) {
          message = `Section actions/buttons couldn't be loaded: ${message}`;
        } else if (request.url.match("/sections/") && isGet(request)) {
          message = `Section couldn't be loaded: ${message}`;
        }

        if (message) {
          this.dispatchNotification(message);
        }
      }
    } catch (e) {
      //FIXME: we don't care about init atm
      // if (!request.url.match("/init")) {
      //   this.dispatchNotification("Incident could not be logged");
      // }
    }
  }

  static async logRequestErrorToBackend(
    error: Error | string,
    request: ResponseContext | ErrorContext,
    response: Response | undefined,
    responseData: string,
    details: string[] = [],
    target: LogTarget
  ) {
    const { user } = getStore().getState();

    details.push(`Request URL: ${request.url}`);

    const logEntry: I.LogEntry = {
      details,
      locationHref: window.location.href,
      responseInfo: {
        httpVerb: request.init.method,
        requestData: this.getRequestData(request),
        responseData,
        statusCode: response?.status,
        uri: response?.url,
      },
      stackTrace: typeof error === "string" ? error : error.stack || "",
      user: user && user.session ? user.session.user : undefined,
      userAgent: navigator.userAgent,
    };

    return logError(logEntry, target);
  }

  static getRequestData(request: ResponseContext | ErrorContext) {
    const { method, body } = request.init;
    if (method !== "PUT" && method !== "POST") {
      return undefined;
    }

    try {
      return body ? body.toString() : undefined;
    } catch (e) {
      return undefined;
    }
  }

  static async logToBackend(error: Error | string, details?: string[]) {
    const { user } = getStore().getState();

    const logEntry: I.LogEntry = {
      details,
      locationHref: window.location.href,
      stackTrace: typeof error === "string" ? error : error.stack || "",
      user: user && user.session ? user.session.user : undefined,
      userAgent: navigator.userAgent,
    };

    return logError(logEntry);
  }
}

export default ExceptionLogger;
